import React from "react";
import { Footer, Navbar } from "../components";
import AdsBannerTop from "../components/AdsBannerTop";
import { Download as DownloadIcon, Smartphone, Play, Star, Zap, Shield, ArrowDown } from "lucide-react";

const Download = () => {
  const features = [
    {
      icon: Play,
      title: "Stream Anywhere",
      description: "Watch your favorite movies and TV shows on the go",
    },
    {
      icon: Star,
      title: "Premium Content",
      description: "Access to the latest releases and exclusive content",
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Quick downloads and seamless streaming experience",
    },
    {
      icon: Shield,
      title: "Safe & Secure",
      description: "Your data is protected with advanced security",
    },
  ];

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <AdsBannerTop />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-24 md:pt-32 pb-16 md:pb-24">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-red/20 via-black to-black"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        
        <div className="relative z-10 padding">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <div className="flex flex-col gap-6 md:gap-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-1 w-12 bg-red"></div>
                <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
                  Mobile App
                </span>
              </div>
              
              <h1 className="text-white poppins font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight">
                Download{" "}
                <span className="text-red">FilmJetHub</span>{" "}
                Mobile App
              </h1>
              
              <p className="text-white/80 mont text-lg md:text-xl leading-relaxed max-w-2xl">
                Experience seamless streaming on the go! Download now for instant access to your favorite content anytime, anywhere.
              </p>

              {/* Download Button */}
              <div className="flex flex-wrap gap-4 mt-4">
                <a 
                  href="/Filmjethub.apk" 
                  download
                  className="group relative overflow-hidden inline-flex items-center gap-3 px-8 py-4 bg-red text-white mont font-semibold text-lg rounded-lg hover:bg-red/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red/30"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    <DownloadIcon className="text-white" size={24} fill="white" />
                    Download APK
                    <ArrowDown size={20} />
                  </span>
                  <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300"></div>
                </a>
              </div>
            </div>

            {/* Mockup Image */}
            <div className="relative flex items-center justify-center lg:justify-end">
              <div className="relative w-full max-w-md lg:max-w-lg">
                <img
                  src="/mockup.svg"
                  alt="FilmJetHub Mobile App Mockup"
                  className="w-full h-auto drop-shadow-2xl"
                />
                {/* Floating Animation Elements */}
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-red/20 rounded-full blur-xl animate-pulse"></div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-red/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="padding py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-1 w-12 bg-red"></div>
              <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
                Features
              </span>
              <div className="h-1 w-12 bg-red"></div>
            </div>
            <h2 className="text-white poppins text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Why Choose FilmJetHub Mobile?
            </h2>
            <p className="text-white/60 mont text-lg max-w-2xl mx-auto">
              Discover the power of entertainment in your pocket with our feature-rich mobile app
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-gray/20 backdrop-blur-sm rounded-lg p-6 border border-white/10 hover:border-red/30 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-red/0 via-red/0 to-red/0 group-hover:from-red/5 group-hover:via-red/5 group-hover:to-red/5 transition-all duration-300 rounded-lg"></div>
                  
                  <div className="relative z-10">
                    <div className="w-14 h-14 rounded-lg bg-red/20 flex items-center justify-center mb-4 group-hover:bg-red/30 transition-colors duration-300">
                      <Icon className="text-red" size={28} />
                    </div>
                    <h3 className="text-white poppins font-semibold text-xl mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-white/60 mont text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="padding py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-gray/20 to-gray/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/10">
            <Smartphone className="text-red mx-auto mb-6" size={64} />
            <h2 className="text-white poppins text-3xl md:text-4xl font-bold mb-4">
              Ready to Start Streaming?
            </h2>
            <p className="text-white/70 mont text-lg mb-8 max-w-2xl mx-auto">
              Download FilmJetHub mobile app now and enjoy unlimited access to movies, TV shows, and more on your device.
            </p>
            <a 
              href="/Filmjethub.apk" 
              download
              className="group relative overflow-hidden inline-flex items-center gap-3 px-10 py-5 bg-red text-white mont font-bold text-xl rounded-lg hover:bg-red/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red/40"
            >
              <span className="relative z-10 flex items-center gap-3">
                <DownloadIcon className="text-white" size={28} fill="white" />
                Download Now
                <ArrowDown size={24} />
              </span>
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300"></div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Download;
